import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Data, Router, UrlTree } from '@angular/router';
import { Store, select } from '@ngrx/store';
import * as _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { environment as env } from '../../../environments/environment';
import { ClientIdTypeEnum } from '../enum/client-id.enum';
import { Menu } from '../models/menu.model';
import { selectAuthoritiesResult, selectUserInfoResult } from '../store/selectors/auth-user-info.selector';
import { selectClientIdResult } from '../store/selectors/client-id.selector';
import { AppStates } from '../store/state/app.states';
import { AuthUserInfoState } from '../store/state/auth-user-info.state';
import { CookiesUtil } from '../utils/cookies-util';

type AuthenticationType = Observable<boolean> | Promise<boolean> | boolean | UrlTree;

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService {
  protected authUserInfo: AuthUserInfoState;
  private authorities: string[];

  constructor(
    public router: Router,
    private readonly store: Store<AppStates>,
    private readonly cookieService: CookieService // private readonly logger: NGXLogger
  ) {
    this.cookiesUtil = new CookiesUtil(cookieService);
    this.store
      .pipe(
        select(selectClientIdResult),
        tap(clientId => (this.clientId = clientId))
      )
      .subscribe();

    this.store.pipe(select(selectUserInfoResult)).subscribe(userInfo => {
      this.authUserInfo = userInfo;
    });

    this.store
      .pipe(
        select(selectAuthoritiesResult),
        filter(value => Boolean(value))
      )
      .subscribe(authorities => (this.authorities = authorities));
  }

  // @ts-ignore
  private clientId: ClientIdTypeEnum | null;
  private readonly cookiesUtil: CookiesUtil;
  // private readonly MAXSIZE = env.cookies.maxSizePerCookies;

  public canActivate(route: ActivatedRouteSnapshot): AuthenticationType {
    return this.doAuthenticated(route.data, route.routeConfig.path);
  }

  public canActivateChild(route: ActivatedRouteSnapshot): AuthenticationType {
    return this.doAuthenticated(route.data, route.routeConfig.path);
  }

  public checkPermission(permission: Array<string> | string, isRegExPattern = false): boolean {
    const permissions: Array<string> = Array.isArray(permission) ? permission : [permission];
    let hasPermission = false;

    this.store
      .select(state => (state.userInfo && state.userInfo.authorities ? state.userInfo.authorities : null))
      .subscribe(authorities => {
        if (permissions && authorities) {
          hasPermission = isRegExPattern
            ? permissions.some(val => this.checkAuthoritiesRegex(val, authorities))
            : permissions.some(val => authorities.indexOf(val) > -1);
        }
      });

    return hasPermission;
  }

  generateMenu(config: any[], userAuthorities: string[]) {
    const menus: Menu[] = [];
    const subLinks = {};

    // Extract routing menu from routing table
    const configFiltered = config
      .map(route =>
        route.path === '' && route.children && route.children.length > 0 ? route.children.shift() : route
      )
      .filter(route => {
        if (route.children) {
          route.children = route.children.filter(child => {
            let hasPermission = true;

            if (child.data && child.data.permissions && child.data.isRegex) {
              hasPermission = child.data.permissions.some(val => this.checkAuthoritiesRegex(val, userAuthorities));
            } else if (child.data && child.data.permissions) {
              hasPermission = child.data.permissions.some(val => userAuthorities.indexOf(val) > -1);
            }

            // In case user doesn't have permission on the first tab so it will automatically redirect to the secondary routing
            if (
              child.path === 'product-list' &&
              !this.checkPermission([
                'assort_inv_v',
                'assort_asset_v',
                'assort_sto_v',
                'assort_inv_m',
                'assort_asset_m',
                'assort_sto_m',
                'ADMIN'
              ])
            ) {
              child.path = 'request-list';
            }

            if (
              child.path === 'store-list' &&
              !this.checkPermission([
                'store_v',
                'store_eq',
                'store_locksale',
                'merchant_edit_m',
                'merchant_order_m',
                'store_terminate',
                'ADMIN'
              ])
            ) {
              child.path = 'store-request-list';
            }

            if (
              child.path === 'delivery-order-list' &&
              !this.checkPermission(
                [
                  '^do_logis_m_[a-zA-Z]{2}[0-9]{1,3}$',
                  '^do_v_[a-zA-Z]{2}[0-9]{1,3}$',
                  '^do_m_[a-zA-Z]{2}[0-9]{1,3}$'
                ],
                true
              )
            ) {
              child.path = 'shipment-list';
            }

            if (
              child.path === 'supplier-group-list' &&
              !this.checkPermission([
                'suppliergroup_inv_v',
                'suppliergroup_asset_v',
                'suppliergroup_sto_v',
                // 'suppliergroup_inv_app',
                // 'suppliergroup_asset_app',
                // 'suppliergroup_sto_app',
                'suppliergroup_inv_m',
                'suppliergroup_asset_m',
                'suppliergroup_sto_m',
                'ADMIN'
              ])
            ) {
              child.path = 'supplier-group-request-list';
            }

            if (
              child.path === 'shelf-list' &&
              !this.checkPermission(['shelf_v', 'shelf_m', 'shelf_firstlot_m', 'ADMIN'])
            ) {
              child.path = 'shelf-request-list';
            }

            if (child.path === 'receive-order-list' && !this.checkPermission(['cr_m', 'ro_v'])) {
              child.path = 'receive-order-list';
            }

            if (child.path === 'reward-active-list' && !this.checkPermission(['rw_m', 'rw_v', 'rw_app'])) {
              child.path = 'reward-request-list';
            }

            if (child.path === 'claim-request-list' && !this.checkPermission(['cr_m', 'cr_v', 'cr_app'])) {
              child.path = 'claim-request-list';
            }

            if (child.data && child.data.subgroup) {
              subLinks[child.data.subgroup] = [...(subLinks[child.data.subgroup] || []), child.path];
            }

            return hasPermission && child.data && child.data.isShowOnMenu;
          });
        } else {
          let hasPermission = true;

          if (route && route.data && route.data.permissions && !route.data.isRegex) {
            hasPermission = route.data.permissions.some(val => userAuthorities.indexOf(val) > -1);
          } else if (route && route.data && route.data.permissions && route.data.isRegex) {
            hasPermission = route.data.permissions.some(val => this.checkAuthoritiesRegex(val, userAuthorities));
          }

          // if (
          //   route.path === 'store-assortment/store-assortment-list' &&
          //   !this.checkPermission(['store_assortment_v', 'store_assortment_m', 'ADMIN'])
          // ) {
          //   route.path = 'store-assortment/store-assortment-request-list';
          // }

          return hasPermission && route.data && route.data.isShowOnMenu;
        }

        return (route.data && route.data.isShowOnMenu) || (route.children && route.children.length > 0);
      })
      .filter(route => (route.children && route.children.length > 0) || !route.children)
      .sort((a, b) => a.data.order - b.data.order);

    configFiltered.forEach(parent => {
      const menu = new Menu();
      menu.title = parent.data.title;
      menu.link = parent.path ? '/' + parent.path : '';

      if (parent.data.group) {
        menu.group = parent.data.group;
      }
      if (parent.data.icon) {
        menu.icon = parent.data.icon;
      }
      if (parent.data.badge) {
        menu.badge = parent.data.badge;
      }
      if (parent.data.active) {
        menu.active = parent.data.active;
      }

      if (parent.children) {
        menu.type = 'dropdown';
        menu.submenus = [];

        parent.children.forEach(child => {
          const subMenu = new Menu();

          subMenu.title = child.data.title ? child.data.title : '';
          subMenu.link = child.path;
          subMenu.sublinks = subLinks[child.data.subgroup];
          subMenu.featureName = child.data.featureName;

          menu.submenus.push(subMenu);
        });
      } else {
        menu.type = 'simple';
      }

      menus.push(menu);
    });

    return menus;
  }

  public getMenuByPermission(userAuthorities: string[]): Menu[] {
    return this.generateMenu(_.cloneDeep(this.router.config), userAuthorities);
  }

  private checkAuthoritiesRegex(strReg: string, authorities: string[]): boolean {
    for (const value of authorities) {
      const regex = new RegExp(strReg, 'gi');
      if (regex.exec(value) !== null) {
        return true;
      }
    }
    return false;
  }

  private doAuthenticated(data: Data, path: string): boolean {
    const accessTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.accessToken);
    let canActivate = this.cookieService.check(accessTokenCookiesName[0]);

    if (canActivate && this.authUserInfo.forceChangePassword) {
      canActivate = path === 'force-change-password';
      if (!canActivate) {
        this.router.navigateByUrl('/force-change-password', {
          replaceUrl: true
        });
      }
    } else if (canActivate && !this.authUserInfo.forceChangePassword && data.isShowOnMenu !== false) {
      // authorities => {
      if (path === 'force-change-password') {
        canActivate = false;
        this.router.navigateByUrl('/dashboard/my-task');
      } else if (!data.permissions) {
        canActivate = true;
      } else {
        if (data.permissions.some(val => this.checkAuthoritiesRegex(val, this.authorities))) {
          return canActivate;
        }
        canActivate = false;
        this.router.navigateByUrl('/dashboard/my-task');
      }
    } else {
      canActivate = false;
      this.router.navigateByUrl('/');
    }
    return canActivate;
  }
}
